import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ChevronLeft, Lock, Pencil, Send } from 'lucide-react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { Card, CardContent } from '@dashboard/components/ui/card';
import { Textarea } from '@dashboard/components/ui/textarea';
import { toast } from '@dashboard/context/ToastContext';
import usePage from '@dashboard/hooks/usePage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import { confirm } from '@dashboard/lib/confirm';
import LegacyButton from '@dashboard/components/Button';
import { Input } from '@dashboard/components/ui/input';
import Stepper from '@dashboard/components/ui/stepper';
import RecipientRows, { newRow, type RecipientRow } from './components/RecipientRows';
import PasteEmailsDisclosure from './components/PasteEmailsDisclosure';
import PastEventsDisclosure from './components/PastEventsDisclosure';
import StaffInviteEmailPreview from './components/StaffInviteEmailPreview';
import StaffSummaryCard from './components/StaffSummaryCard';
import { deriveTicketTypeOptions } from './components/TicketTypeRestriction';
import { useStaffInvites, type InviteResult } from './hooks/useStaffInvites';
import { useStaffInviteMessage } from './hooks/useStaffInviteMessage';
import { useStaffInviteSubject } from './hooks/useStaffInviteSubject';
import { useStaffInviteFrom } from './hooks/useStaffInviteFrom';
import { formatDefaultSubject } from './lib/inviteSubject';
import { EMAIL_RE } from './lib/parseBulkInvites';

interface Props {
  pageId: string;
  sellerId: string;
}

const STEPS = [{ label: 'Add staff' }, { label: 'Review email' }, { label: 'Send' }];

const PREVIEW_CHIP_COUNT = 6;

const RecipientChips: React.FC<{
  emails: string[];
  expanded: boolean;
  onToggleExpanded: () => void;
}> = ({ emails, expanded, onToggleExpanded }) => {
  if (emails.length === 0) return null;
  const visible =
    expanded || emails.length <= PREVIEW_CHIP_COUNT ? emails : emails.slice(0, PREVIEW_CHIP_COUNT);
  const remaining = emails.length - PREVIEW_CHIP_COUNT;
  const showToggle = emails.length > PREVIEW_CHIP_COUNT;
  return (
    <div
      className={`flex flex-wrap gap-1.5 ${expanded && emails.length > 18 ? 'max-h-36 overflow-y-auto' : ''}`}
    >
      {visible.map(email => (
        <Badge key={email} variant="secondary">
          {email}
        </Badge>
      ))}
      {showToggle && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onToggleExpanded}
          className="h-5 px-2 rounded-full border border-dashed border-border text-muted-foreground text-xs hover:border-foreground hover:text-foreground hover:bg-transparent"
        >
          {expanded ? 'Show less' : `+ ${remaining} more`}
        </Button>
      )}
    </div>
  );
};

const InviteStaffWizard: React.FC<Props> = ({ pageId, sellerId }) => {
  const history = useHistory();
  const eventOverviewBase = `/pages/${pageId}`;
  const { page } = usePage();
  const { user, token, isFree } = useAuth();
  const ticketTypeOptions = useMemo(() => deriveTicketTypeOptions(page), [page]);
  const initialMessage = page?.eventDetails?.staffInviteMessage ?? '';
  const displayDefaultSubject = formatDefaultSubject(page?.title);
  const initialSubject = page?.eventDetails?.staffInviteSubject ?? displayDefaultSubject;

  // Wizard state.
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rows, setRows] = useState<RecipientRow[]>([newRow(0)]);
  const [nextId, setNextId] = useState(1);
  const [customNote, setCustomNote] = useState(initialMessage);
  const [subjectLine, setSubjectLine] = useState(initialSubject);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [results, setResults] = useState<InviteResult[] | null>(null);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [chipsExpanded, setChipsExpanded] = useState(false);

  const { invite } = useStaffInvites(pageId, sellerId);
  const { save: saveMessage } = useStaffInviteMessage(pageId, initialMessage);
  const { save: saveSubject } = useStaffInviteSubject(pageId, initialSubject);
  const { data: inviteFrom, isError: inviteFromError } = useStaffInviteFrom(pageId);
  const fromDisplay = inviteFrom?.from ?? (inviteFromError ? '—' : 'Resolving…');

  const filledRows = useMemo(() => rows.filter(r => EMAIL_RE.test(r.email.trim())), [rows]);
  const recipientCount = filledRows.length;
  const subjectInvalid = subjectLine.trim() === '';

  const addRowsFromDisclosure = (
    entries: Array<{ email: string; name?: string; allowedTicketTypes: string[] }>,
  ) => {
    if (entries.length === 0) return;
    setRows(current => {
      const out: RecipientRow[] = [];
      let idCursor = nextId;
      // If the only row is empty, reuse it instead of appending.
      const trimmed = current.filter(r => {
        if (current.length !== 1) return true;
        return r.email.trim() || r.name.trim();
      });
      out.push(...trimmed);
      for (const e of entries) {
        out.push({
          id: idCursor++,
          email: e.email,
          name: e.name ?? '',
          allowedTicketTypes: e.allowedTicketTypes,
        });
      }
      setNextId(idCursor);
      return out;
    });
  };

  const dedupeRows = (input: RecipientRow[]): { rows: RecipientRow[]; removed: number } => {
    const seen = new Map<string, number>();
    const next: RecipientRow[] = [];
    let removed = 0;
    for (const r of input) {
      const email = r.email.trim().toLowerCase();
      if (!email) {
        next.push(r);
        continue;
      }
      const winnerIdx = seen.get(email);
      if (winnerIdx === undefined) {
        seen.set(email, next.length);
        next.push(r);
        continue;
      }
      const winner = next[winnerIdx];
      next[winnerIdx] = {
        ...winner,
        name: r.name.trim() ? r.name : winner.name,
        allowedTicketTypes:
          r.allowedTicketTypes.length > 0 ? r.allowedTicketTypes : winner.allowedTicketTypes,
      };
      removed += 1;
    }
    return { rows: next, removed };
  };

  const goToStep = (n: 1 | 2 | 3) => {
    if (step === 1 && n > 1) {
      const { rows: deduped, removed } = dedupeRows(rows);
      if (removed > 0) {
        setRows(deduped);
        toast({ title: `Removed ${removed} duplicate email${removed === 1 ? '' : 's'}` });
      }
    }
    setStep(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNoteChange = (next: string) => {
    setCustomNote(next);
    setUnsavedChanges(true);
  };

  const handleSubjectChange = (next: string) => {
    setSubjectLine(next);
    setUnsavedChanges(true);
  };

  const handleSaveAll = async () => {
    setSavingChanges(true);
    try {
      await Promise.all([saveSubject(subjectLine), saveMessage(customNote)]);
      setPreviewVersion(v => v + 1);
      setUnsavedChanges(false);
      toast({ title: 'Saved' });
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toast({
        title: "Couldn't save",
        description: message ?? 'Try again in a moment.',
        type: 'alert',
      });
    } finally {
      setSavingChanges(false);
    }
  };

  const flushPendingEdits = async () => {
    if (!unsavedChanges) return;
    await Promise.all([saveSubject(subjectLine), saveMessage(customNote)]);
    setUnsavedChanges(false);
  };

  const handleSendTest = async () => {
    setTestSending(true);
    try {
      await flushPendingEdits();
      const data = (await fetcher(`/scanner/${pageId}/staff-invite-test`, token, {
        method: 'POST',
      })) as { ok: true; sentTo: string };
      toast({
        title: `Test email sent to ${data?.sentTo ?? user?.email ?? 'you'}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toast({
        title: "Couldn't send test email",
        description: message ?? 'Try again in a moment.',
        type: 'alert',
      });
    } finally {
      setTestSending(false);
    }
  };

  const handleSend = async () => {
    if (recipientCount === 0) return;
    setSending(true);
    try {
      await flushPendingEdits();
      const out = await invite(
        filledRows.map(r => ({
          email: r.email.trim(),
          ...(r.name.trim() ? { name: r.name.trim() } : {}),
          ...(r.allowedTicketTypes.length > 0 ? { allowedTicketTypes: r.allowedTicketTypes } : {}),
        })),
      );
      const sent = out.filter(r => r.outcome !== 'error').length;
      if (sent === 0) {
        toast({ title: "Couldn't send invites", type: 'alert' });
        return;
      }
      setResults(out);
      toast({
        title: `Invited ${sent} staff member${sent === 1 ? '' : 's'}`,
      });
    } finally {
      setSending(false);
    }
  };

  const restart = () => {
    setRows([newRow(nextId)]);
    setNextId(n => n + 1);
    setResults(null);
    setStep(1);
  };

  const finishToManage = () => {
    history.push(`${eventOverviewBase}/staff/manage`);
  };

  const { state: locationState } = useLocation<{ from?: string } | undefined>();
  const hasWork =
    results === null && (step > 1 || rows.some(row => row.email.trim() || row.name.trim()));
  // Nothing is sent until the last step, so leaving with staff entered throws that work away.
  const goBack = async () => {
    if (hasWork) {
      const discard = await confirm('Discard unsaved changes?', { confirm: 'Discard' });
      if (!discard) return;
    }
    history.push(locationState?.from ?? `${eventOverviewBase}/staff/manage`);
  };

  const backButton = (
    <div className="absolute top-6 left-4">
      <LegacyButton appearance="light" onClick={goBack} aria-label="Back to the event">
        <ChevronLeftIcon className="h-5 w-5" />
      </LegacyButton>
    </div>
  );

  const isSent = results !== null;
  const sentCount = results?.filter(r => r.outcome !== 'error').length ?? 0;
  const failedCount = (results?.length ?? 0) - sentCount;

  if (isFree) {
    return (
      <div className="mx-auto mt-8 mb-36 w-full max-w-screen-2xl px-4 sm:px-16">
        {backButton}
        <main className="flex justify-center">
          <div className="w-full max-w-2xl mt-20 mb-20">
            <div className="mb-6 text-center">
              <h1 className="text-xl text-balance">Invite staff</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Invite staff to check in guests at your event.
              </p>
            </div>

            <div className="rounded-md border border-input p-4 flex items-start gap-3 max-w-md mx-auto">
              <Lock className="size-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-black dark:text-foreground">
                    Event staff are available on paid plans
                  </p>
                  <p className="text-sm text-gray-700 dark:text-muted-foreground">
                    The Free plan doesn't include event staff. Upgrade to invite staff to check in
                    guests at your event.
                  </p>
                </div>
                <Button to="/upgrade?upgradeFlow=event-staff" size="sm">
                  Upgrade
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-8 mb-36 w-full max-w-screen-2xl px-4 sm:px-16">
      {backButton}
      <main className="flex justify-center">
        <div className="w-full max-w-2xl mt-20 mb-20">
          <div className="mb-6 text-center">
            <h1 className="text-xl text-balance">Invite staff</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Invite staff to check in guests at your event.
            </p>
          </div>

          <div className="mb-6">
            <Stepper
              steps={STEPS}
              current={isSent ? 3 : step}
              onStepClick={isSent ? undefined : n => goToStep(n as 1 | 2 | 3)}
            />
          </div>

          {step === 1 && (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h2 className="text-sm font-semibold mb-3">Staff</h2>
                  <RecipientRows
                    rows={rows}
                    onChange={setRows}
                    nextId={nextId}
                    onAdvanceNextId={() => setNextId(n => n + 1)}
                    ticketTypeOptions={ticketTypeOptions}
                  />
                </div>

                <div className="pt-2 border-t border-border divide-y divide-border">
                  <PasteEmailsDisclosure
                    ticketTypeOptions={ticketTypeOptions}
                    onAdd={addRowsFromDisclosure}
                  />
                  <PastEventsDisclosure
                    sellerId={sellerId}
                    pageId={pageId}
                    ticketTypeOptions={ticketTypeOptions}
                    onAdd={addRowsFromDisclosure}
                  />
                </div>

                <div className="pt-5 border-t border-border flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {recipientCount > 0
                      ? `${recipientCount} staff member${recipientCount === 1 ? '' : 's'} ready to invite`
                      : null}
                  </div>
                  <Button type="button" onClick={() => goToStep(2)} disabled={recipientCount === 0}>
                    Next: Review email
                    <ArrowRight className="size-3.5 ml-1.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 1 && <StaffSummaryCard pageId={pageId} />}

          {step === 2 && (
            <>
              <Card>
                <CardContent className="p-6 space-y-5">
                  <div>
                    <h2 className="text-base font-semibold">Review the invite email</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      This is what your staff will receive. Add a note if you want to give them
                      extra context.
                    </p>
                  </div>

                  <div className="pb-5 border-b border-border space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-sm">
                        Sending to{' '}
                        <span className="font-medium">
                          {recipientCount} staff member{recipientCount === 1 ? '' : 's'}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => goToStep(1)}
                        className="h-7 px-2 text-xs text-muted-foreground"
                      >
                        <Pencil className="size-3 mr-1.5" />
                        Edit staff
                      </Button>
                    </div>
                    <RecipientChips
                      emails={filledRows.map(r => r.email.trim())}
                      expanded={chipsExpanded}
                      onToggleExpanded={() => setChipsExpanded(e => !e)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="subjectLine" className="block text-sm font-medium">
                      Subject
                    </label>
                    <Input
                      id="subjectLine"
                      placeholder={displayDefaultSubject}
                      value={subjectLine}
                      maxLength={200}
                      onChange={e => handleSubjectChange(e.target.value)}
                      aria-invalid={subjectInvalid || undefined}
                      required
                    />
                    {subjectInvalid && (
                      <p className="text-xs text-destructive">Subject is required.</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="customNote" className="block text-sm font-medium">
                      Custom note{' '}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <Textarea
                      id="customNote"
                      rows={3}
                      placeholder="Arrival details, dress code, or who to ask for on arrival. Example: <strong>Doors open at 7pm</strong>"
                      value={customNote}
                      onChange={e => handleNoteChange(e.target.value)}
                    />
                    <div className="flex justify-end pt-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveAll}
                        disabled={savingChanges || !unsavedChanges || subjectInvalid}
                      >
                        {savingChanges ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Email preview</label>
                    <div className="h-[60vh] rounded-lg overflow-hidden border border-border">
                      <StaffInviteEmailPreview key={previewVersion} pageId={pageId} />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <div className="flex flex-col items-end gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSendTest}
                        disabled={testSending || subjectInvalid}
                      >
                        <Send className="size-3.5 mr-1.5" />
                        {testSending ? 'Sending…' : 'Send test email'}
                      </Button>
                      <p className="text-[0.7rem] text-muted-foreground">
                        Sending to{' '}
                        <span className="font-medium text-foreground">
                          {user?.email ?? 'your account email'}
                        </span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-4 flex items-center justify-between">
                <Button type="button" variant="outline" onClick={() => goToStep(1)}>
                  <ChevronLeft className="size-3.5 mr-1.5" />
                  Back
                </Button>
                <Button type="button" onClick={() => goToStep(3)} disabled={subjectInvalid}>
                  Next: Review &amp; send
                  <ArrowRight className="size-3.5 ml-1.5" />
                </Button>
              </div>
            </>
          )}

          {step === 3 && !isSent && (
            <>
              <Card>
                <CardContent className="p-6 space-y-5">
                  <h2 className="text-base font-semibold">Ready to send</h2>

                  <dl className="rounded-lg border border-border bg-muted/30 divide-y divide-border text-sm">
                    <div className="flex items-start justify-between px-4 py-3">
                      <dt className="text-muted-foreground">Recipients</dt>
                      <dd className="font-medium text-right">
                        {recipientCount} staff member{recipientCount === 1 ? '' : 's'}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between px-4 py-3">
                      <dt className="text-muted-foreground">Subject</dt>
                      <dd className="font-medium text-right">
                        {subjectLine.trim() || displayDefaultSubject}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between px-4 py-3">
                      <dt className="text-muted-foreground">Custom note</dt>
                      <dd
                        className={`font-medium text-right ${customNote.trim() ? '' : 'text-muted-foreground'}`}
                      >
                        {customNote.trim() ? 'Included' : 'Not included'}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between px-4 py-3">
                      <dt className="text-muted-foreground">From</dt>
                      <dd
                        className={`font-medium text-right ${inviteFrom?.from ? '' : 'text-muted-foreground'}`}
                      >
                        {fromDisplay}
                      </dd>
                    </div>
                  </dl>

                  <p className="text-xs text-muted-foreground">
                    Each staff member receives a unique check-in link. You can track invitations in
                    manage staff.
                  </p>
                </CardContent>
              </Card>

              <div className="mt-4 flex items-center justify-between">
                <Button type="button" variant="outline" onClick={() => goToStep(2)}>
                  <ChevronLeft className="size-3.5 mr-1.5" />
                  Back
                </Button>
                <Button type="button" onClick={handleSend} disabled={sending}>
                  <Send className="size-3.5 mr-1.5" />
                  {sending
                    ? 'Sending…'
                    : `Send ${recipientCount} invite${recipientCount === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          )}

          {step === 3 && isSent && (
            <Card>
              <CardContent className="p-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-muted text-foreground flex items-center justify-center mb-3 animate-in fade-in zoom-in-50 duration-300">
                  <CheckCircle2 className="size-6 animate-in zoom-in-0 duration-500 delay-150 fill-mode-both" />
                </div>
                <h2 className="text-xl font-semibold">Invites sent</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {sentCount === 1
                    ? '1 invite has been emailed.'
                    : `${sentCount} invites have been emailed.`}{' '}
                  {failedCount > 0 &&
                    `${failedCount} ${failedCount === 1 ? "invite couldn't" : "invites couldn't"} be sent.`}{' '}
                  You can track who&apos;s accepted in{' '}
                  <button
                    type="button"
                    onClick={finishToManage}
                    className="font-medium text-foreground underline underline-offset-2"
                  >
                    Manage staff
                  </button>
                  .
                </p>
                <div className="mt-6 flex items-center justify-center gap-2">
                  <Button type="button" variant="outline" onClick={restart}>
                    Invite more
                  </Button>
                  <Button type="button" onClick={finishToManage}>
                    Manage staff
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default InviteStaffWizard;
